import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel, SectionTitle, SportBadge, EmptyState, Overline } from "@/components/portal/Bits";
import { COLORS } from "@/lib/format";
import { TrendingUp, TrendingDown } from "lucide-react";

export default function StockWatch() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/stock-watch");
      setData(data);
    })();
  }, []);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div>
      <SectionTitle sub={data.note}>Stock Watch</SectionTitle>

      {data.ranked.length === 0 ? (
        <EmptyState
          testId="stockwatch-empty"
          icon={TrendingUp}
          title="No ranked athletes yet"
          hint="Athletes rank once two weekly snapshots with engagement + follower data exist. Ranking uses engagement rate and growth — never raw follower counts."
        />
      ) : (
        <Panel className="overflow-hidden mb-8">
          <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-border">
            <Overline className="col-span-1">#</Overline>
            <Overline className="col-span-5">Athlete</Overline>
            <Overline className="col-span-2 text-right">Eng. rate</Overline>
            <Overline className="col-span-2 text-right">Growth</Overline>
            <Overline className="col-span-2 text-right">Score</Overline>
          </div>
          {data.ranked.map((r) => (
            <Link key={r.athlete_id} to={`/athlete/${r.athlete_id}`}
              className="grid grid-cols-12 gap-2 px-5 py-4 border-b border-border/60 last:border-0 hover:bg-secondary/60 transition-colors items-center"
              data-testid={`stock-row-${r.athlete_id}`}>
              <div className="col-span-1 font-display text-2xl text-white">{r.rank}</div>
              <div className="col-span-5">
                <div className="font-semibold text-white">{r.name}</div>
                <div className="mt-1"><SportBadge sport={r.sport} /></div>
              </div>
              <div className="col-span-2 text-right text-white font-semibold">{r.engagement_rate}%</div>
              <div className="col-span-2 text-right font-semibold inline-flex items-center justify-end gap-1"
                style={{ color: r.growth_pct >= 0 ? COLORS.positive : COLORS.negative }}>
                {r.growth_pct >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {r.growth_pct}%
              </div>
              <div className="col-span-2 text-right font-display text-xl text-white">{r.score}</div>
            </Link>
          ))}
        </Panel>
      )}

      {data.unranked.length > 0 && (
        <div>
          <Overline className="mb-3">Awaiting data ({data.unranked.length})</Overline>
          <div className="flex flex-wrap gap-2" data-testid="stockwatch-unranked">
            {data.unranked.map((r) => (
              <Link key={r.athlete_id} to={`/athlete/${r.athlete_id}`}
                className="inline-flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-white transition-colors">
                {r.name}
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">No data yet</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
